import { Outlet, Link, useLocation, useNavigate } from 'react-router';
import { LayoutDashboard, FolderKanban, Bug, BarChart3, MessageSquare, Menu, X, Settings, Clock, Users, DollarSign, Bell, Zap, Calendar, LogOut, Home, Moon, Sun, Type, Star, Newspaper, ListTodo, Megaphone, User, Search } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import { OrganizationSwitcher } from './OrganizationSwitcher';
import { useAuth } from '../providers/AuthProvider';
import { useTheme } from '../contexts/ThemeContext';
import { usePreferences } from '../contexts/PreferencesContext';
import { Button } from './ui/button';
import { useApp } from '../providers/AppProvider';
import { getOrgFromUrl, normalizeOrganizationId } from '../utils/urlToOrg';
import { DiligentSlimBanner } from './DiligentSlimBanner';
import { ProfileCompletionPrompt } from './ProfileCompletionPrompt';
import { SystemSearch } from './SystemSearch';
import { parseDateLike } from '../utils/dateUtils';
import { getUserIdentitySet, normalizeUserIdentity } from '../utils/userProfile';
import { useOrganizationData } from '../hooks/useOrganizationData';
import {
  applyOrganizationColorScheme,
  resolveOrganizationAppearance,
} from '../constants/organizationTheme';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';

export default function RootLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [organizationName, setOrganizationName] = useState('');
  const { appUser, firebaseUser, logout } = useAuth();
  const { theme, setTheme, effectiveTheme, isUserThemeLocked } = useTheme();
  const { preferences, setFontSize, isFavoriteProject, toggleFavoriteProject } = usePreferences();
  const [favoriteProjects, setFavoriteProjects] = useState<any[]>([]);
  const { state } = useApp();
  const { tasks: scopedTasks } = useOrganizationData(state, { includeAllOrganizations: true }) as {
    tasks: any[];
  };

  useEffect(() => {
    if (!state) {
      setOrganizationName('No Organization');
      setFavoriteProjects([]);
      return;
    }

    setOrganizationName(state.currentOrganization?.name || 'No Organization');
    
    // Load favorite projects
    const allProjects = state.projects || [];
    const favProjects = allProjects.filter((p: any) => isFavoriteProject(p.id));
    setFavoriteProjects(favProjects.slice(0, 5)); // Show max 5
  }, [location, isFavoriteProject, state]);

  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileSearchOpen(false);
  }, [location.pathname]);
  
  const getUserInitials = () => {
    if (appUser?.name) {
      return appUser.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return 'U';
  };
  
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const navigation = [
    { name: 'Dashboard', href: '/app', icon: LayoutDashboard },
    { name: 'Projects', href: '/app/projects', icon: FolderKanban },
    { name: 'Calendar', href: '/app/calendar', icon: Calendar },
    { name: 'Quarterly Expenses', href: '/app/quarterly', icon: DollarSign, restrictedToAffiliatedOrg: true },
    { name: 'Notifications', href: '/app/notifications', icon: Bell, restrictedToAffiliatedOrg: true },
    { name: 'Bug Tracker', href: '/app/bugs', icon: Bug },
    { name: 'Reports', href: '/app/reports', icon: BarChart3 },
    { name: 'Retrospectives', href: '/app/retrospectives', icon: MessageSquare },
    { name: 'News Feed', href: '/app/newsfeed', icon: Newspaper, restrictedToAffiliatedOrg: true },
  ];

  const quickLinks = [
    { name: 'Planning Poker', href: '/app/planning-poker', icon: Zap },
    { name: 'My Timesheet', href: '/app/timesheet', icon: Clock },
    { name: 'Team Reports', href: '/app/team-reports', icon: Users },
  ];

  const bottomLinks = [
    { name: 'Settings', href: '/app/settings', icon: Settings, restrictedToAffiliatedOrg: true },
  ];

  const isActive = (path: string) => {
    if (path === '/app') return location.pathname === '/app';
    return location.pathname.startsWith(path);
  };

  const urlOrgId = normalizeOrganizationId(getOrgFromUrl());
  const isAdminUser = state?.currentUser?.email === 'admin@gmail.com';
  const showOrganizationSwitcher = Boolean(state?.currentOrganization) &&
    (state?.organizations?.length ?? 0) > 0 &&
    (!urlOrgId || isAdminUser);

  const isUserAffiliatedWithUrlOrg = useMemo(() => {
    if (!urlOrgId || !state?.currentUser) {
      return true;
    }

    const affiliatedOrgIds = new Set<string>();

    const addOrgId = (value: unknown) => {
      const normalized = normalizeOrganizationId(String(value || '').trim());
      if (normalized) {
        affiliatedOrgIds.add(normalized);
      }
    };

    addOrgId(state.currentUser.organizationId);

    const orgEntries = Array.isArray(state.currentUser.organizations)
      ? state.currentUser.organizations
      : [];

    orgEntries.forEach((entry) => {
      if (typeof entry === 'string') {
        addOrgId(entry);
        return;
      }

      if (entry && typeof entry === 'object') {
        const organizationEntry = entry as { id?: string; organizationId?: string; orgId?: string };
        addOrgId(organizationEntry.id || organizationEntry.organizationId || organizationEntry.orgId);
      }
    });

    return affiliatedOrgIds.has(urlOrgId);
  }, [urlOrgId, state?.currentUser]);

  const visibleNavigation = useMemo(() => {
    return navigation.filter((item) => isUserAffiliatedWithUrlOrg || !item.restrictedToAffiliatedOrg);
  }, [isUserAffiliatedWithUrlOrg, navigation]);

  const visibleBottomLinks = useMemo(() => {
    return bottomLinks.filter((item) => isUserAffiliatedWithUrlOrg || !item.restrictedToAffiliatedOrg);
  }, [bottomLinks, isUserAffiliatedWithUrlOrg]);

  const organizationAppearance = useMemo(
    () => resolveOrganizationAppearance(state?.currentOrganization),
    [state?.currentOrganization]
  );

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    applyOrganizationColorScheme(
      document.documentElement,
      organizationAppearance.colorScheme,
      effectiveTheme
    );
  }, [effectiveTheme, organizationAppearance.colorScheme]);

  useEffect(() => {
    if (!organizationAppearance.defaultTheme || isUserThemeLocked) {
      return;
    }

    if (theme !== organizationAppearance.defaultTheme) {
      setTheme(organizationAppearance.defaultTheme, { source: 'organization' });
    }
  }, [organizationAppearance.defaultTheme, isUserThemeLocked, setTheme, theme]);

  const headerSummary = useMemo(() => {
    if (!state?.currentUser) {
      return {
        activeTasksCount: 0,
        dueSoonCount: 0,
        unreadNotificationsCount: 0,
      };
    }

    const identitySet = getUserIdentitySet(state.currentUser);
    const now = new Date();
    const dueSoonBoundary = new Date(now);
    dueSoonBoundary.setDate(dueSoonBoundary.getDate() + 3);

    const activeTasksCount = (scopedTasks || []).filter(
      (task) => task.status === 'Active' || task.status === 'In Progress'
    ).length;

    const dueSoonCount = (scopedTasks || []).filter((task) => {
      if (['Completed', 'Deferred', 'Not Required'].includes(task.status)) {
        return false;
      }

      const dueDate = parseDateLike(
        task.targetCompletionDate || task.targetEndDate || task.nextUpdateDate
      );

      if (!dueDate) {
        return false;
      }

      const dueTime = dueDate.getTime();
      return dueTime >= now.getTime() && dueTime <= dueSoonBoundary.getTime();
    }).length;

    const unreadNotificationsCount = (state.notifications || []).filter(
      (notification) =>
        !notification.read && identitySet.has(normalizeUserIdentity(notification.userId))
    ).length;

    return {
      activeTasksCount,
      dueSoonCount,
      unreadNotificationsCount,
    };
  }, [state?.currentUser, state?.notifications, scopedTasks]);

  const profileMenuSurfaceClassName =
    'w-56 border-slate-200 bg-white text-slate-900 shadow-lg dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100';
  const profileMenuItemClassName =
    'cursor-pointer text-slate-700 focus:bg-slate-100 focus:text-slate-900 dark:text-slate-200 dark:focus:bg-slate-800 dark:focus:text-slate-100';
  const profileMenuSubTriggerClassName =
    'text-slate-700 focus:bg-slate-100 focus:text-slate-900 data-[state=open]:bg-slate-100 data-[state=open]:text-slate-900 dark:text-slate-200 dark:focus:bg-slate-800 dark:focus:text-slate-100 dark:data-[state=open]:bg-slate-800 dark:data-[state=open]:text-slate-100';
  const profileMenuRadioItemClassName =
    'text-slate-700 focus:bg-slate-100 focus:text-slate-900 dark:text-slate-200 dark:focus:bg-slate-800 dark:focus:text-slate-100';

  return (
    <div className="app-shell">
      {/* Mobile menu button */}
      <div className="app-shell-bar fixed top-0 left-0 right-0 z-50 flex h-14 items-center justify-between gap-2 border-b px-3 lg:hidden">
        <div className="min-w-0 flex-1">
          <DiligentSlimBanner
            organizationName={organizationName}
            role={appUser?.role}
          />
        </div>
        <div className="flex items-center gap-1">
          {state?.currentUser && (
            <button
              onClick={() => {
                setMobileSearchOpen((prev) => !prev);
                setMobileMenuOpen(false);
              }}
              className="rounded-md p-2 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800"
              aria-label="Toggle system search"
              title="System search"
            >
              <Search className="w-5 h-5" />
            </button>
          )}
          <button
            onClick={() => {
              setMobileMenuOpen(!mobileMenuOpen);
              setMobileSearchOpen(false);
            }}
            className="rounded-md p-2 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800"
            aria-label="Toggle navigation menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileSearchOpen && state?.currentUser && (
        <div className="app-shell-search-panel fixed top-14 left-0 right-0 z-40 border-b p-2 lg:hidden">
          <SystemSearch
            projects={state.projects || []}
            tasks={state.tasks || []}
            users={state.users || []}
            currentUser={state.currentUser}
          />
        </div>
      )}

      {/* Sidebar */}
      <aside
        className={`app-shell-sidebar fixed inset-y-0 left-0 z-40 w-64 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:block pt-16 lg:pt-0`}
      >
        <div className="relative h-full flex flex-col">
          <div className="pointer-events-none absolute right-0 bottom-0 top-14 hidden w-px bg-slate-200 dark:bg-slate-700 lg:block" />
          <div className="hidden h-14 items-center border-b border-slate-200 px-4 dark:border-slate-700 lg:flex">
            <DiligentSlimBanner
              className="w-full"
            />
          </div>
          {showOrganizationSwitcher && (
            <div className="border-b border-slate-200 p-4 dark:border-slate-700">
              <OrganizationSwitcher />
            </div>
          )}
          
          {/* Main Navigation */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-4">
              <div className="mb-2 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Main
              </div>
              <nav className="space-y-1">
                {visibleNavigation.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.href);
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                        active
                          ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300'
                          : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span>{item.name}</span>
                    </Link>
                  );
                })}
              </nav>
            </div>

            {/* Quick Links */}
            <div className="border-t border-slate-200 p-4 dark:border-slate-700">
              <div className="mb-2 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Quick Access
              </div>
              <nav className="space-y-1">
                {quickLinks.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.href);
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                        active
                          ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300'
                          : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span>{item.name}</span>
                    </Link>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Bottom Links */}
          <div className="border-t border-slate-200 p-4 dark:border-slate-700">
            <nav className="space-y-1">
              {visibleBottomLinks.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.href);
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      active
                        ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300'
                        : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{item.name}</span>
                  </Link>
                );
              })}
            </nav>

            <div className="mt-4 border-t border-slate-200 pt-4 dark:border-slate-700 lg:hidden">
              <div className="mb-2 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Appearance
              </div>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setTheme('light', { source: 'user' });
                    setMobileMenuOpen(false);
                  }}
                  disabled={isUserThemeLocked}
                  className={`inline-flex items-center justify-center gap-1 rounded-md border px-2 py-2 text-xs font-medium transition-colors ${
                    theme === 'light'
                      ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-700 dark:bg-blue-950/40 dark:text-blue-300'
                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800'
                  } ${isUserThemeLocked ? 'cursor-not-allowed opacity-60' : ''}`}
                >
                  <Sun className="h-3.5 w-3.5" />
                  <span>Light</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setTheme('dark', { source: 'user' });
                    setMobileMenuOpen(false);
                  }}
                  disabled={isUserThemeLocked}
                  className={`inline-flex items-center justify-center gap-1 rounded-md border px-2 py-2 text-xs font-medium transition-colors ${
                    theme === 'dark'
                      ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-700 dark:bg-blue-950/40 dark:text-blue-300'
                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800'
                  } ${isUserThemeLocked ? 'cursor-not-allowed opacity-60' : ''}`}
                >
                  <Moon className="h-3.5 w-3.5" />
                  <span>Dark</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setTheme('system', { source: 'user' });
                    setMobileMenuOpen(false);
                  }}
                  disabled={isUserThemeLocked}
                  className={`inline-flex items-center justify-center gap-1 rounded-md border px-2 py-2 text-xs font-medium transition-colors ${
                    theme === 'system'
                      ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-700 dark:bg-blue-950/40 dark:text-blue-300'
                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800'
                  } ${isUserThemeLocked ? 'cursor-not-allowed opacity-60' : ''}`}
                >
                  <Sun className="h-3.5 w-3.5 opacity-70" />
                  <span>System</span>
                </button>
              </div>
              {isUserThemeLocked && (
                <p className="mt-2 px-4 text-xs text-slate-500 dark:text-slate-400">
                  Theme is managed by your organization.
                </p>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/55 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Desktop Header with Organization Name */}
      <div className="app-shell-bar fixed top-0 left-64 right-0 z-20 hidden h-14 border-b px-6 lg:block">
        <div className="flex h-full items-center gap-4 mx-auto">
          <div className="w-full max-w-2xl">
            {state?.currentUser && (
              <SystemSearch
                projects={state.projects || []}
                tasks={state.tasks || []}
                users={state.users || []}
                currentUser={state.currentUser}
              />
            )}
          </div>
          {state?.currentUser && (
            <div className="hidden xl:flex items-center gap-2">
              <Link
                to="/app/dashboard"
                className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-medium text-slate-700 transition-colors hover:border-slate-300 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
              >
                {headerSummary.activeTasksCount} active
              </Link>
              <Link
                to="/app/calendar"
                className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700 transition-colors hover:border-amber-300 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300 dark:hover:bg-amber-900/50"
              >
                {headerSummary.dueSoonCount} due in 3d
              </Link>
              {isUserAffiliatedWithUrlOrg && (
                <Link
                  to="/app/notifications"
                  className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 transition-colors hover:border-blue-300 hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300 dark:hover:bg-blue-900/50"
                >
                  {headerSummary.unreadNotificationsCount} unread
                </Link>
              )}
            </div>
          )}
          <div className="ml-auto flex items-center gap-4">
            {/* User Profile Dropdown */}
            {firebaseUser && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="relative h-9 w-9 rounded-full text-slate-600 hover:bg-slate-100 hover:text-slate-800 focus-visible:ring-slate-300 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-slate-100 dark:focus-visible:ring-slate-600"
                  >
                    <Avatar className="h-9 w-9">
                      <AvatarImage src={firebaseUser.photoURL || appUser?.avatar} alt={appUser?.name || 'User'} />
                      <AvatarFallback className="bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">{getUserInitials()}</AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className={profileMenuSurfaceClassName} align="end" forceMount>
                  <DropdownMenuLabel className="font-normal text-slate-800 dark:text-slate-100">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">{appUser?.name || firebaseUser.displayName}</p>
                      <p className="text-xs leading-none text-slate-500 dark:text-slate-400">
                        {appUser?.email || firebaseUser.email}
                      </p>
                      <p className="mt-1 text-xs leading-none text-slate-500 dark:text-slate-400">
                        {appUser?.role}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-slate-200 dark:bg-slate-700" />
                  
                  {/* Home Link */}
                  <DropdownMenuItem asChild className={profileMenuItemClassName}>
                    <Link to="/app" className="cursor-pointer">
                      <Home className="mr-2 h-4 w-4" />
                      <span>Home</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className={profileMenuItemClassName}>
                    <Link to="/app/profile" className="cursor-pointer">
                      <User className="mr-2 h-4 w-4" />
                      <span>My Profile</span>
                    </Link>
                  </DropdownMenuItem>
                  
                  <DropdownMenuSeparator className="bg-slate-200 dark:bg-slate-700" />
                  
                  {/* Favorite Projects */}
                  {favoriteProjects.length > 0 && (
                    <>
                      <DropdownMenuLabel className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400">
                        Favorite Projects
                      </DropdownMenuLabel>
                      {favoriteProjects.map((project) => (
                        <DropdownMenuItem key={project.id} asChild className={profileMenuItemClassName}>
                          <Link to={`/app/projects/${project.id}`} className="cursor-pointer pl-8">
                            <Star className="mr-2 h-3 w-3 fill-current text-amber-500 dark:text-amber-400" />
                            <span className="text-sm truncate">{project.name}</span>
                          </Link>
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuSeparator className="bg-slate-200 dark:bg-slate-700" />
                    </>
                  )}
                  
                  {/* Theme Toggle */}
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger className={profileMenuSubTriggerClassName}>
                      {theme === 'dark' ? (
                        <Moon className="mr-2 h-4 w-4" />
                      ) : theme === 'system' ? (
                        <Sun className="mr-2 h-4 w-4 opacity-60" />
                      ) : (
                        <Sun className="mr-2 h-4 w-4" />
                      )}
                      <span>Theme</span>
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent className="border-slate-200 bg-white text-slate-900 shadow-lg dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
                      <DropdownMenuRadioGroup value={theme} onValueChange={(value) => setTheme(value as 'light' | 'dark' | 'system', { source: 'user' })}>
                        <DropdownMenuRadioItem value="light" className={profileMenuRadioItemClassName}>
                          <Sun className="mr-2 h-4 w-4" />
                          Light
                        </DropdownMenuRadioItem>
                        <DropdownMenuRadioItem value="dark" className={profileMenuRadioItemClassName}>
                          <Moon className="mr-2 h-4 w-4" />
                          Dark
                        </DropdownMenuRadioItem>
                        <DropdownMenuRadioItem value="system" className={profileMenuRadioItemClassName}>
                          <Sun className="mr-2 h-4 w-4 opacity-60" />
                          System Default
                        </DropdownMenuRadioItem>
                      </DropdownMenuRadioGroup>
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                  
                  {/* Font Size */}
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger className={profileMenuSubTriggerClassName}>
                      <Type className="mr-2 h-4 w-4" />
                      <span>Font Size</span>
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent className="border-slate-200 bg-white text-slate-900 shadow-lg dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
                      <DropdownMenuRadioGroup value={preferences.fontSize} onValueChange={(value) => setFontSize(value as any)}>
                        <DropdownMenuRadioItem value="small" className={profileMenuRadioItemClassName}>Small</DropdownMenuRadioItem>
                        <DropdownMenuRadioItem value="medium" className={profileMenuRadioItemClassName}>Medium</DropdownMenuRadioItem>
                        <DropdownMenuRadioItem value="large" className={profileMenuRadioItemClassName}>Large</DropdownMenuRadioItem>
                      </DropdownMenuRadioGroup>
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                  
                  <DropdownMenuSeparator className="bg-slate-200 dark:bg-slate-700" />
                  
                  {/* Settings and Timesheet */}
                  {isUserAffiliatedWithUrlOrg && (
                    <DropdownMenuItem asChild className={profileMenuItemClassName}>
                      <Link to="/app/settings" className="cursor-pointer">
                        <Settings className="mr-2 h-4 w-4" />
                        <span>Settings</span>
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem asChild className={profileMenuItemClassName}>
                    <Link to="/app/timesheet" className="cursor-pointer">
                      <Clock className="mr-2 h-4 w-4" />
                      <span>My Timesheet</span>
                    </Link>
                  </DropdownMenuItem>
                  
                  <DropdownMenuSeparator className="bg-slate-200 dark:bg-slate-700" />
                  
                  {/* Logout */}
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="cursor-pointer text-red-600 focus:bg-red-50 focus:text-red-700 dark:text-red-400 dark:focus:bg-red-950/40 dark:focus:text-red-300"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Sign out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>

      {/* Mobile bottom navigation bar */}
      <nav className="app-shell-bar fixed bottom-0 left-0 right-0 z-50 border-t lg:hidden">
        <div className="grid grid-cols-6 h-14">
          {[
            { icon: LayoutDashboard, label: 'Home',     href: '/app' },
            { icon: ListTodo,        label: 'My Tasks', href: '/app/dashboard' },
            { icon: Megaphone,       label: 'Standup',  href: (() => {
              const m = location.pathname.match(/\/app\/projects\/([^/]+)/);
              return m ? `/app/projects/${m[1]}/standup` : '/app/projects';
            })() },
            { icon: Clock,           label: 'Hours',    href: '/app/timesheet' },
            { icon: User,            label: 'Profile',  href: '/app/profile' },
            { icon: LogOut,          label: 'Logout',   action: handleLogout },
          ].map(({ icon: Icon, label, href, action }) => {
            const active = Boolean(href) && (location.pathname === href || (href !== '/app' && location.pathname.startsWith(href)));
            if (action) {
              return (
                <button
                  key={label}
                  type="button"
                  onClick={action}
                  className="flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium text-slate-500 transition-colors hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                >
                  <Icon className="w-5 h-5" />
                  <span>{label}</span>
                </button>
              );
            }
            return (
              <Link
                key={String(href)}
                to={href as string}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium transition-colors ${
                  active ? 'text-blue-600 dark:text-blue-300' : 'text-slate-500 dark:text-slate-400'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span>{label}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Main content */}
      <main className={`relative min-h-screen lg:ml-64 ${mobileSearchOpen ? 'pt-28' : 'pt-14'} lg:pt-14 pb-14 lg:pb-0`}>
        <ProfileCompletionPrompt user={state?.currentUser ?? null} />
        <Outlet />
      </main>
    </div>
  );
}